// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import addReorderPadFilters from "@adl/App/Handlers/ReorderPad/AddReorderPadFilters";
import { getReorderPadDataViewProperty } from "@adl/App/Store/Selectors/ReorderPadSelectors";
import { DivWrapper } from "@adl/XDL/constants";
import { color } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker/DatePicker";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapStateToProps = (state: ApplicationState) => {
    const parsedQuery = parseQueryString<{ fromDate?: string; toDate?: string }>(getLocation(state).search);
    return {
        loaded: !!getReorderPadDataViewProperty(state, "items"),
        fromDate: parsedQuery.fromDate ? new Date(parsedQuery.fromDate.replace(/-/g, "/")) : undefined,
        toDate: parsedQuery.toDate ? new Date(parsedQuery.toDate.replace(/-/g, "/")) : undefined,
    };
};

const mapDispatchToProps = {
    addReorderPadFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ReorderPadDateRangeStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
}

export const dateRangeStyles: ReorderPadDateRangeStyles = {
    wrapper: {
        css: css`
            padding: 12px;
            background: ${color("common.background")};
            border: 1px solid ${color("common.border")};
            border-radius: 2px;
            ${DivWrapper}:not(:empty) {
                margin-top: 16px;
            }
        `,
    },
    titleText: {
        variant: "h6",
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
                margin-bottom: 10px;
            `,
        },
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const styles = dateRangeStyles;

const ReorderPadDateRange: FC<Props> = ({ toDate, fromDate, loaded, addReorderPadFilters }) => {
    if (!loaded) {
        return null;
    }

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (!selectedDay) {
            return;
        }
        addReorderPadFilters({
            fromDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (!selectedDay) {
            return;
        }
        addReorderPadFilters({
            toDate: convertDateToDateOnlyString(selectedDay),
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography forwardAs="label" {...styles.titleText}>
                {translate("Date Range")}
            </Typography>
            <DatePicker
                {...styles.fromDate}
                label={translate("From")}
                selectedDay={fromDate}
                onDayChange={fromDateChangeHandler}
                dateTimePickerProps={{
                    clearIcon: null,
                    maxDate: toDate,
                    ...styles.fromDate?.dateTimePickerProps,
                }}
            />
            <DatePicker
                {...styles.toDate}
                label={translate("To")}
                selectedDay={toDate}
                onDayChange={toDateChangeHandler}
                dateTimePickerProps={{
                    clearIcon: null,
                    minDate: fromDate,
                    ...styles.toDate?.dateTimePickerProps,
                }}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPadDateRange);
